import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { HTTPService } from "./../../services/httpService";

const API = new HTTPService();

export const CheckOut = ({ show, onClose, onComplete, installer, invoice, payment }) => {
  const [paymentState, setPaymentState] = useState("initializing");
  const [terminal, setTerminal] = useState("checking");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // FIXED: Auto-check terminal status when modal opens
  const checkTerminal = useCallback(async (terminalId) => {
    try {
      //console.log("Auto-checking terminal ID:", terminalId);
      setLoading(true);
      setError(null);
      
      const response = await API.get(`/terminal/${terminalId}`);
      //console.log("Terminal response:", response);
      
      // FIXED: Validate response structure
      if (response.data?.success && response.data?.data?.status === "online") {
        setTerminal("online");
        // Auto-initiate payment once terminal is confirmed online
        setTimeout(() => initiatePayment(), 500);
      } else {
        setTerminal("offline");
        setPaymentState("terminal_offline");
        setError(response.data?.error || "Terminal is offline");
      }
    } catch (err) {
      console.error("Terminal check failed:", err);
      setTerminal("offline");
      setPaymentState("terminal_error");
      setError(`Terminal check failed: ${err.response?.data?.details || err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // FIXED: Auto-initiate payment when terminal is ready
  const initiatePayment = useCallback(async () => {
    if (!payment || !installer?.stripe_location) return;
    
    try {
      //console.log("Auto-initiating payment");
      setPaymentState("sending_to_reader");
      setLoading(true);
      
      const newPayment = {
        paymentId: payment,
        terminalId: installer.stripe_location,
      };

      const { data: response } = await API.post(`/terminalPay/`, newPayment);
      
      //console.log("Payment sent to terminal:", response);
      
      // FIXED: Validate response structure
      if (!response.success) {
        throw new Error(response.error || 'Failed to send payment to terminal');
      }
      
      setPaymentState("waiting_for_customer");
      
      // Start auto-polling for payment status
      startPaymentPolling();
      
    } catch (err) {
      console.error("Payment initiation failed:", err);
      setPaymentState("payment_error");
      setError(`Payment failed: ${err.response?.data?.details || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [payment, installer?.stripe_location]);

  // FIXED: Auto-poll payment status with proper response validation
  const checkPaymentStatus = useCallback(async () => {
    if (!payment) return null;
    
    try {
      const { data: response } = await API.get(`/payments/${payment}`);
      
      //console.log("Payment status response:", response);
      
      // FIXED: Handle new response structure
      if (!response.success) {
        console.error("Payment status check failed:", response.error);
        return "error";
      }
      
      const status = response.status;
      
      if (status === "succeeded") {
        // REMOVED: Client-side invoice settlement for terminal payments
        // The webhook now handles marking invoices as paid when charge.succeeded fires
        // This prevents race conditions where the client might not see the payment status in time
        setPaymentState("success");
        return "succeeded";
      } else if (status === "payment_failed") {
        setPaymentState("failed");
        setError("Payment was declined");
        return "failed";
      }
      
      return status;
    } catch (err) {
      console.error("Payment status check failed:", err);
      return "error";
    }
  }, [payment, invoice?.id]);

  // Enhanced polling mechanism with better error handling
  const startPaymentPolling = useCallback(() => {
    const pollInterval = setInterval(async () => {
      const status = await checkPaymentStatus();
      
      if (status === "succeeded" || status === "failed" || status === "error") {
        clearInterval(pollInterval);
        
        if (status === "succeeded") {
          // Auto-close and notify success
          setTimeout(() => {
            onComplete();
          }, 2000);
        }
      }
    }, 2000);

    // Cleanup after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (paymentState === "waiting_for_customer") {
        setPaymentState("timeout");
        setError("Payment timed out. Please try again.");
      }
    }, 300000);

    return pollInterval;
  }, [checkPaymentStatus, paymentState, onComplete]);

  // FIXED: Auto-retry with exponential backoff
  const autoRetry = useCallback(async (operation, maxRetries = 3) => {
    if (retryCount >= maxRetries) return false;
    
    const delay = Math.pow(2, retryCount) * 1000;
    setTimeout(() => {
      setRetryCount(prev => prev + 1);
      operation();
    }, delay);
    
    return true;
  }, [retryCount]);

  // Manual retry for persistent failures
  const handleRetry = () => {
    setRetryCount(0);
    setError(null);
    setPaymentState("initializing");
    
    if (terminal === "offline") {
      checkTerminal(installer?.stripe_location);
    } else {
      initiatePayment();
    }
  };

  // FIXED: Cancel payment attempt
  const handleCancel = async () => {
    try {
      const { data: response } = await API.post(`/terminal/${installer?.stripe_location}/cancel`);
      
      if (response.success) {
        setPaymentState("cancelled");
      } else {
        console.error("Cancel failed:", response.error);
        setPaymentState("cancelled"); // Still allow closing
      }
    } catch (err) {
      console.error("Cancel failed:", err);
      setPaymentState("cancelled"); // Still allow closing
    }
  };

  // Auto-start process when modal opens
  useEffect(() => {
    if (show && installer?.stripe_location && payment) {
      setPaymentState("initializing");
      setTerminal("checking");
      setRetryCount(0);
      setError(null);
      
      // Start the automated flow
      setTimeout(() => {
        checkTerminal(installer.stripe_location);
      }, 500);
    }
  }, [show, installer?.stripe_location, payment, checkTerminal]);

  // ENHANCED: Get display messages based on current state
  const getDisplayMessages = () => {
    const amount = invoice?.amount_due ? `$${(invoice.amount_due / 100).toFixed(2)}` : '';
    const customer = invoice?.customer_name || '';
    
    switch (paymentState) {
      case "initializing":
        return {
          primary: "Initializing payment...",
          secondary: "Please wait",
          showSpinner: true
        };
      case "terminal_offline":
        return {
          primary: "Card reader is offline",
          secondary: "Please check that the reader is powered on and connected",
          showRetry: true
        };
      case "terminal_error":
        return {
          primary: "Unable to connect to reader",
          secondary: "Please check connection and try again",
          showRetry: true
        };
      case "sending_to_reader":
        return {
          primary: "Sending payment to reader...",
          secondary: `Amount: ${amount}`,
          showSpinner: true
        };
      case "waiting_for_customer":
        return {
          primary: "Present card to reader",
          secondary: `${amount} for ${customer}`,
          showCancel: true,
          showSpinner: true
        };
      case "processing":
        return {
          primary: "Processing payment...",
          secondary: "Please wait",
          showSpinner: true
        };
      case "payment_error":
        return {
          primary: "Payment failed to send",
          secondary: "Please try again",
          showRetry: true
        };
      case "failed":
        return {
          primary: "Payment declined",
          secondary: "Card was declined by bank",
          showRetry: true
        };
      case "success":
        return {
          primary: "Payment successful!",
          secondary: `${amount} charged to customer`,
          showClose: true
        };
      case "cancelled":
        return {
          primary: "Payment cancelled",
          secondary: "",
          showClose: true
        };
      case "timeout":
        return {
          primary: "Payment timed out",
          secondary: "Please try again",
          showRetry: true
        };
      default:
        return {
          primary: terminal === "checking" ? "Checking card reader..." : `Amount Due: ${amount}`,
          secondary: terminal === "checking" ? "" : `for ${customer}`,
          showSpinner: terminal === "checking"
        };
    }
  };

  const messages = getDisplayMessages();
  const imageName = require('./../../assets/Jobvious_logo.png');

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton={["success", "cancelled", "failed", "timeout"].includes(paymentState)}>
        <div className="logosize text-center mb-3 mt-2">
          <img src={imageName} alt="Jobvious Logo" />
        </div>
      </Modal.Header>
      <Modal.Body className="text-center">
        <div style={{ minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {messages.showSpinner && (
            <div className="mb-3">
              <Spinner animation="border" role="status" variant="primary">
                <span className="sr-only">Loading...</span>
              </Spinner>
            </div>
          )}
          
          <h3 className="mb-2">{messages.primary}</h3>
          {messages.secondary && <h5 className="text-muted mb-3">{messages.secondary}</h5>}
          
          {error && (
            <div className="alert alert-danger mt-2" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}
          
          <div className="mt-3">
            {messages.showRetry && (
              <Button 
                variant="primary" 
                onClick={handleRetry}
                disabled={loading}
                className="me-2"
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Retrying...
                  </>
                ) : (
                  'Try Again'
                )}
              </Button>
            )}
            
            {messages.showCancel && (
              <Button 
                variant="outline-secondary" 
                onClick={handleCancel}
                disabled={loading}
                className="me-2"
              >
                Cancel Payment
              </Button>
            )}
            
            {messages.showClose && (
              <Button 
                variant="success" 
                onClick={onComplete}
                size="lg"
              >
                {paymentState === "success" ? "Complete" : "Close"}
              </Button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};